'use client';

import React, { useState, useEffect } from 'react';
import { 
  Loader2, 
  ExternalLink, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Check,
  Pin
} from 'lucide-react';

const Daftar = () => {
  // --- STATE MANAGEMENT ---
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());

  // --- DATA TIMELINE ---
  const timelineData = [
    {
      id: 1,
      title: "Pendaftaran & Tes Tertulis",
      date: "26 Nov - 7 Des 2025",
      desc: "Isi formulir lengkap dan kerjakan soal tes tertulis.",
      startDate: new Date('2025-11-26'),
      endDate: new Date('2025-12-07T23:59:59')
    },
    {
      id: 2,
      title: "Pengumuman Tes Tertulis",
      date: "10 Desember 2025",
      desc: "Hasil seleksi tahap 1 diumumkan via Instagram.",
      startDate: new Date('2025-12-10'),
      endDate: new Date('2025-12-10T23:59:59')
    },
    {
      id: 3,
      title: "Tes Wawancara",
      date: "13 - 14 Desember 2025",
      desc: "Sesi wawancara tatap muka/daring.",
      startDate: new Date('2025-12-13'),
      endDate: new Date('2025-12-14T23:59:59')
    },
    {
      id: 4,
      title: "Pengumuman Final",
      date: "19 Desember 2025",
      desc: "Welcome Party pengurus baru FKPO 2026!",
      startDate: new Date('2025-12-19'),
      endDate: new Date('2025-12-31') 
    }
  ];

  // --- DATA PERSYARATAN ---
  const requirements = [
    "Pengurus aktif OSIS/MPK se-Kabupaten Kulonprogo",
    "Aktif, berintegritas, dan siap berkomitmen",
    "Mengikuti seluruh rangkaian seleksi"
  ];

  useEffect(() => {
    setCurrentDate(new Date());
  }, []);

  // Helper untuk Status Timeline
  const getStepStatus = (step) => {
    const now = currentDate.getTime();
    const start = step.startDate.getTime();
    const end = step.endDate.getTime();

    if (now > end) return 'completed';
    if (now >= start && now <= end) return 'active';
    return 'upcoming';
  };

  return (
    <div 
      className="min-h-screen bg-slate-950 bg-fixed bg-cover bg-center relative font-sans selection:bg-indigo-500 selection:text-white"
      style={{ backgroundImage: `url('/bg.jpg')` }} // Pastikan path gambar benar
    >
      {/* Dark Overlay with Blur */}
      <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-sm z-0"></div>

      <main className="relative z-10 flex-grow py-20 md:py-28 text-slate-200">
        <div className="container mx-auto px-4 md:px-6">
          
          <div className="flex flex-col lg:flex-row gap-12 xl:gap-20">
            
            {/* --- KOLOM KIRI: INFO & TIMELINE (Sticky) --- */}
            <div className="lg:w-5/12">
              <div className="lg:sticky lg:top-28 space-y-10">
                
                {/* Header Section */}
                <div className="space-y-6">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-bold tracking-widest uppercase shadow-[0_0_20px_rgba(99,102,241,0.2)]">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                    </span>
                    Open Recruitment 2026
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight">
                    Mari Berkarya <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400">
                      Bersama FKPO
                    </span>
                  </h1>
                  
                  <p className="text-lg text-slate-400 leading-relaxed max-w-md">
                    Jangan lewatkan kesempatan untuk menjadi bagian dari sejarah. Siapkan dirimu, isi formulir dengan teliti, dan tunjukkan potensi terbaikmu.
                  </p>
                </div>

                {/* Timeline Component Stylish */}
                <div className="relative">
                  {/* Decorative Line Background */}
                  <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-indigo-500/0 via-slate-700 to-indigo-500/0"></div>

                  <div className="space-y-8">
                    {timelineData.map((step) => {
                      const status = getStepStatus(step);
                      
                      let iconColor = "bg-slate-800 text-slate-500 border-slate-700";
                      let titleClass = "text-slate-400";
                      let descClass = "text-slate-500";
                      let cardBorder = "border-transparent";

                      if (status === 'completed') {
                        iconColor = "bg-emerald-500/10 text-emerald-400 border-emerald-500/50";
                        titleClass = "text-emerald-400";
                      } else if (status === 'active') {
                        iconColor = "bg-indigo-600 text-white border-indigo-400 shadow-[0_0_15px_rgba(99,102,241,0.5)]";
                        titleClass = "text-white";
                        descClass = "text-slate-300";
                        cardBorder = "bg-slate-800/40 border-slate-700/50";
                      }

                      return (
                        <div key={step.id} className={`relative flex gap-6 p-4 rounded-xl transition-all duration-300 ${cardBorder}`}>
                          {/* Icon Marker */}
                          <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${iconColor}`}>
                            {status === 'completed' ? <CheckCircle2 size={18} /> : 
                             status === 'active' ? <Clock size={18} className="animate-pulse" /> : 
                             <div className="w-2.5 h-2.5 rounded-full bg-slate-600" />}
                          </div>

                          {/* Content */}
                          <div className="flex-grow pt-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-bold uppercase tracking-wider ${status === 'active' ? 'text-indigo-400' : 'text-slate-500'}`}>
                                {step.date}
                              </span>
                              {status === 'active' && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-500 text-white">NOW</span>
                              )}
                            </div>
                            <h4 className={`text-lg font-bold mb-1 transition-colors ${titleClass}`}>
                              {step.title}
                            </h4>
                            <p className={`text-sm leading-relaxed transition-colors ${descClass}`}>
                              {step.desc}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

              </div>
            </div>

            {/* --- KOLOM KANAN: FORMULIR & SYARAT --- */}
            <div className="lg:w-7/12 flex flex-col gap-6 relative">
              
              {/* === SECTION BARU: PERSYARATAN UMUM === */}
              {/* Tampil sebagai kartu glassmorphism di atas form */}
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-500 to-indigo-500 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-500"></div>
                <div className="relative bg-slate-900/80 backdrop-blur-xl border border-white/10 p-6 md:p-8 rounded-xl shadow-2xl">
                  
                  <div className="flex items-center gap-3 mb-4 border-b border-white/5 pb-4">
                     <div className="p-2 bg-indigo-500/20 rounded-lg text-indigo-400">
                        <Pin size={20} className="transform -rotate-12" />
                     </div>
                     <h3 className="text-xl font-bold text-white tracking-wide">
                       Persyaratan Umum
                     </h3>
                  </div>

                  <ul className="grid gap-3">
                    {requirements.map((req, index) => (
                      <li key={index} className="flex items-start gap-3 group/item">
                        <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 group-hover/item:bg-indigo-500 group-hover/item:text-white transition-colors duration-300">
                           <Check size={12} strokeWidth={3} />
                        </div>
                        <span className="text-slate-300 text-sm md:text-base font-medium leading-relaxed group-hover/item:text-white transition-colors">
                          {req}
                        </span>
                      </li>
                    ))}
                  </ul>

                </div>
              </div>
              {/* ======================================== */}

              {/* Ambient Glow Effect Behind Form */}
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-cyan-500 rounded-2xl blur opacity-20 animate-pulse-slow"></div>

                {/* Browser Window Style Container */}
                <div className="relative bg-slate-900 rounded-xl overflow-hidden shadow-2xl border border-slate-700/50 flex flex-col h-[1300px]">
                  
                  {/* Browser Header Bar */}
                  <div className="h-12 bg-slate-800 border-b border-slate-700 flex items-center px-4 justify-between flex-shrink-0">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500/80 hover:bg-red-500 transition-colors"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500/80 hover:bg-yellow-500 transition-colors"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500/80 hover:bg-green-500 transition-colors"></div>
                    </div>
                    
                    {/* Fake Address Bar */}
                    <div className="flex-grow mx-4 md:mx-8">
                      <div className="bg-slate-950/50 rounded-md py-1 px-3 flex items-center justify-center text-xs text-slate-400 font-mono border border-slate-700/30">
                        <span className="text-green-500 mr-2">🔒 https://</span>
                        forms.google.com/fkpo-recruitment-2026
                      </div>
                    </div>

                    <div className="w-10"></div> {/* Spacer for symmetry */}
                  </div>

                  {/* Form Content Area */}
                  <div className="relative flex-grow bg-white w-full">
                    
                    {/* Loading Spinner Overlay */}
                    {!iframeLoaded && (
                      <div className="absolute inset-0 bg-slate-50 flex flex-col items-center justify-center z-10 transition-opacity duration-500">
                        <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
                        <p className="text-slate-500 font-medium animate-pulse">Memuat Formulir Pendaftaran...</p>
                      </div>
                    )}

                    <iframe 
                      src="https://docs.google.com/forms/d/e/1FAIpQLSeId25PN2KIhgek6592VA4nl2d61loxiXspDlzOgj1EGY4rIw/viewform?embedded=true" 
                      width="100%" 
                      height="100%" 
                      frameBorder="0" 
                      marginHeight="0" 
                      marginWidth="0"
                      title="Formulir Pendaftaran FKPO"
                      onLoad={() => setIframeLoaded(true)}
                      className={`w-full h-full block transition-opacity duration-700 ${iframeLoaded ? 'opacity-100' : 'opacity-0'}`}
                    >
                      Sedang memuat formulir pendaftaran...
                    </iframe>
                  </div>
                </div>
              </div>

              {/* Troubleshooting Card */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 flex flex-col sm:flex-row items-center justify-between gap-4 backdrop-blur-sm">
                <div className="flex items-center gap-3 text-slate-400">
                  <AlertCircle className="w-5 h-5 text-indigo-400 flex-shrink-0" />
                  <p className="text-sm">
                    Formulir tidak muncul atau bermasalah?
                  </p>
                </div>
                <a 
                  href="https://docs.google.com/forms/d/e/1FAIpQLSeId25PN2KIhgek6592VA4nl2d61loxiXspDlzOgj1EGY4rIw/viewform" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-all shadow-lg hover:shadow-indigo-500/25 whitespace-nowrap"
                >
                  Buka di Tab Baru <ExternalLink size={14} />
                </a>
              </div>

            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Daftar;